import { getPlanAlertCopy, getPlanBadgeCopy } from '@/shared/copy/plans';
import type { OrganisationEntitlementsDto, ProjectDto } from '@flowqa/shared';

type ProjectPlanOption = Pick<
  ProjectDto,
  'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'createdAt'
>;

export type PlanAlertTone = 'orange' | 'red';

export type PlanAlert = {
  key: string;
  tone: PlanAlertTone;
  message: string;
};

export type PlanBadge = {
  label: string;
  tone: 'orange' | 'red';
};

export type ProjectPlanOptionState = ProjectPlanOption & {
  locked: boolean;
  lockReason: string | null;
};

export type ResolvedPlanStatus = {
  alerts: PlanAlert[];
  badge: PlanBadge | null;
  projectOptions: ProjectPlanOptionState[];
  currentProjectLocked: boolean;
  uploadBlockedReason: string | null;
};

type ResolvePlanStatusInput = {
  entitlements: OrganisationEntitlementsDto | null;
  projects: ProjectPlanOption[];
  selectedProjectId: string | null;
  now?: Date;
};

const projectLockReason = 'Uploads are locked for this project on the current plan.';

const buildUsageAlertMessage = (executionCount: number, executionLimit: number): string => {
  const usagePercent = Math.round((executionCount / executionLimit) * 100);
  return `${executionCount} / ${executionLimit} (${usagePercent}%) run uploads used. Further run uploads will be blocked when limit is reached.`;
};

const formatRetryDate = (date: Date | null): string | null => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
  }).format(date);
};

const sortProjectsByCreatedAt = (projects: ProjectPlanOption[]): ProjectPlanOption[] =>
  [...projects].sort((left, right) => {
    const leftTime =
      left.createdAt instanceof Date ? left.createdAt.getTime() : Number.MAX_SAFE_INTEGER;
    const rightTime =
      right.createdAt instanceof Date ? right.createdAt.getTime() : Number.MAX_SAFE_INTEGER;
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }
    return left.id.localeCompare(right.id);
  });

const resolveUsageAlert = (
  entitlements: OrganisationEntitlementsDto,
): { alert: PlanAlert | null; badge: PlanBadge | null } => {
  const executionLimit = entitlements.usage.executionLimit;
  if (executionLimit === null || executionLimit <= 0) {
    return { alert: null, badge: null };
  }

  const usageRatio = entitlements.usage.executionCount / executionLimit;
  if (usageRatio >= 0.9) {
    return {
      alert: {
        key: `usage-90-${entitlements.usage.periodEnd.toISOString()}`,
        tone: 'red',
        message: buildUsageAlertMessage(entitlements.usage.executionCount, executionLimit),
      },
      badge: { label: '90% Used', tone: 'red' },
    };
  }
  if (usageRatio >= 0.8) {
    return {
      alert: {
        key: `usage-80-${entitlements.usage.periodEnd.toISOString()}`,
        tone: 'orange',
        message: buildUsageAlertMessage(entitlements.usage.executionCount, executionLimit),
      },
      badge: { label: getPlanBadgeCopy('usage80'), tone: 'orange' },
    };
  }

  return { alert: null, badge: null };
};

const resolveMemberAccessAlert = (
  entitlements: OrganisationEntitlementsDto,
  now: Date,
): { alert: PlanAlert | null; badge: PlanBadge | null } => {
  if (entitlements.plan.code !== 'solo' || entitlements.access.isPrimaryMember) {
    return { alert: null, badge: null };
  }

  const graceEndsAt = entitlements.access.memberGraceEndsAt;
  if (entitlements.access.currentUserWriteAccess && graceEndsAt instanceof Date) {
    const remainingDays = Math.max(
      0,
      Math.ceil((graceEndsAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)),
    );
    return {
      alert: {
        key: `member-grace-${graceEndsAt.toISOString()}`,
        tone: 'orange',
        message: getPlanAlertCopy('memberGrace', { remainingDays }),
      },
      badge: { label: getPlanBadgeCopy('grace'), tone: 'orange' },
    };
  }

  if (!entitlements.access.currentUserWriteAccess) {
    return {
      alert: {
        key: 'member-locked',
        tone: 'red',
        message: getPlanAlertCopy('memberAccessLocked'),
      },
      badge: { label: getPlanBadgeCopy('locked'), tone: 'red' },
    };
  }

  return { alert: null, badge: null };
};

const resolveBillingAlert = (
  entitlements: OrganisationEntitlementsDto,
): { alert: PlanAlert | null; badge: PlanBadge | null } => {
  if (entitlements.subscription.status !== 'past_due') {
    return { alert: null, badge: null };
  }

  if (entitlements.subscription.billingUploadAccessBlocked) {
    return {
      alert: {
        key: 'billing-upload-locked',
        tone: 'red',
        message: getPlanAlertCopy('billingUploadLocked'),
      },
      badge: { label: getPlanBadgeCopy('billing'), tone: 'red' },
    };
  }

  return {
    alert: {
      key: `billing-past-due-${
        entitlements.subscription.billingNextPaymentAttemptAt?.toISOString() ?? 'pending'
      }`,
      tone: 'orange',
      message: getPlanAlertCopy('billingPastDue', {
        retryLabel: formatRetryDate(entitlements.subscription.billingNextPaymentAttemptAt),
      }),
    },
    badge: { label: getPlanBadgeCopy('billing'), tone: 'orange' },
  };
};

const resolveProjectOptions = (
  projects: ProjectPlanOption[],
  entitlements: OrganisationEntitlementsDto | null,
): ProjectPlanOptionState[] => {
  const projectLimit = entitlements?.plan.limits.projectLimit ?? null;
  if (projectLimit === null) {
    return projects.map((project) => ({ ...project, locked: false, lockReason: null }));
  }

  const orderedProjects = sortProjectsByCreatedAt(projects);
  const unlockedIds = new Set(orderedProjects.slice(0, projectLimit).map((project) => project.id));
  return projects.map((project) => {
    const locked = !unlockedIds.has(project.id);
    return {
      ...project,
      locked,
      lockReason: locked ? projectLockReason : null,
    };
  });
};

const resolveUploadBlockedReason = (
  entitlements: OrganisationEntitlementsDto | null,
  currentProjectLocked: boolean,
): string | null => {
  if (entitlements === null) {
    return null;
  }

  if (!entitlements.access.currentUserWriteAccess) {
    return 'Only the earliest member can upload runs on the Solo plan.';
  }

  if (entitlements.subscription.billingUploadAccessBlocked) {
    return 'Uploads are disabled until the outstanding subscription payment is resolved.';
  }

  const executionLimit = entitlements.usage.executionLimit;
  if (
    executionLimit !== null &&
    executionLimit > 0 &&
    entitlements.usage.executionCount >= executionLimit
  ) {
    return 'Your plan run limit has been reached. Upgrade to continue uploading.';
  }

  if (currentProjectLocked) {
    return projectLockReason;
  }

  return null;
};

export const resolvePlanStatus = ({
  entitlements,
  projects,
  selectedProjectId,
  now = new Date(),
}: ResolvePlanStatusInput): ResolvedPlanStatus => {
  const projectOptions = resolveProjectOptions(projects, entitlements);
  const currentProjectLocked =
    selectedProjectId === null
      ? false
      : (projectOptions.find((project) => project.id === selectedProjectId)?.locked ?? false);

  if (entitlements === null) {
    return {
      alerts: [],
      badge: null,
      projectOptions,
      currentProjectLocked,
      uploadBlockedReason: currentProjectLocked ? projectLockReason : null,
    };
  }

  const usageStatus = resolveUsageAlert(entitlements);
  const memberStatus = resolveMemberAccessAlert(entitlements, now);
  const billingStatus = resolveBillingAlert(entitlements);
  const alerts = [billingStatus.alert, memberStatus.alert, usageStatus.alert].filter(
    (alert): alert is PlanAlert => alert !== null,
  );
  const badge = billingStatus.badge ?? memberStatus.badge ?? usageStatus.badge ?? null;

  return {
    alerts,
    badge,
    projectOptions,
    currentProjectLocked,
    uploadBlockedReason: resolveUploadBlockedReason(entitlements, currentProjectLocked),
  };
};
